import { FC, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';

import {
  CreditCardStatusCode,
  FeatureFilterField,
  FeatureName,
  UserFilterValues,
} from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import usePaymentCardUpdatedSubscription from 'common/hooks/usePaymentCardUpdatedSubscription';
import useTeamRequestsUpdatedSubscription from 'common/hooks/useTeamRequestsUpdatedSubscription';
import { sortByStatusCodes } from 'common/paymentCards/utils';
import { useTrackLoadingState } from 'common/sentry/webAppOpeningTransaction';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { useFakeTemporaryVirtualPaymentCardIdContext } from 'features/PaymentCards/context';
import * as Layout from 'layouts/ContentLayout';

import useCreditCards from '../../hooks/useCreditCards';
import useOngoingPaymentCardsRequests from '../../hooks/useOngoingPaymentCardsRequests';
import * as routes from '../../routes';
import { useAutomaticVirtualCardOrder } from '../PaymentCardOrder/PhysicalCardOrder/hooks/useAutomaticVirtualCardOrder';
import FilterBar from './components/FilterBar';
import OngoingPaymentCardRequestRow from './components/OngoingPaymentCardRequestRow';
import OngoingRequestSidebar from './components/OngoingRequestSidebar';
import PaymentCardListEmptyState from './components/PaymentCardListEmptyState';
import PaymentCardRow from './components/PaymentCardRow';
import PaymentCardSidebar from './components/PaymentCardSidebar';
import useTeamMembersCount from './hooks/useTeamMembersCount';
import locales from './locales';
import { CardFilters } from './types';

// Lists of definitively and non definitively blocked status codes.
// The order of the status codes defines the sorting in the cards list
const DEFINITIVELY_BLOCKED_STATUS_CODES = [
  CreditCardStatusCode.Lost,
  CreditCardStatusCode.Stolen,
  CreditCardStatusCode.Deactivated,
];

const NOT_DEFINITIVELY_BLOCKED_STATUS_CODES = [
  CreditCardStatusCode.Created,
  CreditCardStatusCode.Active,
  CreditCardStatusCode.Locked,
  CreditCardStatusCode.Inactive,
];

interface PaymentCardListParams {
  creditCardId: string;
}

const PaymentCardList: FC = () => {
  const fakeTemporaryVirtualPaymentCardId =
    useFakeTemporaryVirtualPaymentCardIdContext();
  const { creditCardId } = useParams<PaymentCardListParams>();
  const {
    companyCards,
    refetch: paymentCardsRefetch,
    userCards,
    ...creditCards
  } = useCreditCards({
    fetchPolicy: 'network-only',
  });
  const { isUniqueMember, ...teamMembers } = useTeamMembersCount();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  usePaymentCardUpdatedSubscription();

  // For an AH/Admin, all the company's ongoing payment cards requests will be
  // listed. And for an Employee, only their requests will be displayed thanks
  // to the authorization system.
  const { ongoingPaymentCardsRequests, ...ongoingPaymentCardsRequestsQuery } =
    useOngoingPaymentCardsRequests();
  const history = useHistory();
  const { state } = useLocation<{
    shouldDisplayBackButton?: true;
  } | null>();

  useTeamRequestsUpdatedSubscription({
    onData: ({ data }) => {
      if (data.data) {
        ongoingPaymentCardsRequestsQuery.refetch();
        paymentCardsRefetch();
      }
    },
  });

  // If the feature-flag is enabled, always use the "COMPANY" filter rather
  // than the "OWN" one.
  // This will not cause issues for Employee who cannot see every card of
  // the company because the endpoint will only return the cards they are
  // allowed to see (theirs).
  const [filter, setFilter] = useState<CardFilters>(CardFilters.COMPANY);

  const isListEmpty =
    companyCards.length === 0 && ongoingPaymentCardsRequests.length === 0;

  const selectedOngoingPaymentCardRequest = useMemo(
    () =>
      ongoingPaymentCardsRequests.find(
        (ongoingPaymentCardsRequest) =>
          ongoingPaymentCardsRequest.teamMembersRequestId === creditCardId,
      ),
    [creditCardId, ongoingPaymentCardsRequests],
  );

  const {
    error: pendingAutomaticVirtualCardError,
    loading: pendingAutomaticVirtualCardLoading,
    shouldAutoOrderVirtualCard,
  } = useAutomaticVirtualCardOrder({ fetchPolicy: 'network-only' });

  const [firstPhysicalCard] = userCards
    .filter((c) => c.isPhysical)
    .sort((c) => c.createdAt);

  const fakeTemporaryVirtualCard = {
    ...firstPhysicalCard,
    creditCardId: fakeTemporaryVirtualPaymentCardId,
    isPhysical: false,
  };

  const isLoading =
    teamMembers.loading ||
    creditCards.loading ||
    ongoingPaymentCardsRequestsQuery.loading ||
    pendingAutomaticVirtualCardLoading;

  /**
   * Track this page as being fully loaded.
   * @warning please ensure all loaders that impact the rendering of the meaningful content of this page are provided to this hook.
   */
  useTrackLoadingState({
    destination: 'payment-cards-list',
    isLoaded: !isLoading,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (
    teamMembers.error ||
    creditCards.error ||
    ongoingPaymentCardsRequestsQuery.error ||
    pendingAutomaticVirtualCardError
  ) {
    return <ErrorView />;
  }

  const canRequestPaymentCards = canAccessFeature({
    name: FeatureName.RequestPaymentcards,
    where: {
      or: [{ field: FeatureFilterField.User, values: [UserFilterValues.Self] }],
    },
  });
  const canOrderPaymentCards = canAccessFeature({
    name: FeatureName.PaymentcardsOrder,
  });

  const userCardsWithFakeTemporaryVirtualCard = [
    ...userCards,
    fakeTemporaryVirtualCard,
  ];
  const companyCardsWithFakeTemporaryVirtualCard = [
    ...companyCards,
    fakeTemporaryVirtualCard,
  ];

  const selectedCreditCard = shouldAutoOrderVirtualCard
    ? companyCardsWithFakeTemporaryVirtualCard.find(
        (card) => card.creditCardId === creditCardId,
      )
    : companyCards.find((card) => card.creditCardId === creditCardId);

  const filteredCards = () => {
    if (shouldAutoOrderVirtualCard) {
      return filter === CardFilters.COMPANY
        ? companyCardsWithFakeTemporaryVirtualCard
        : userCardsWithFakeTemporaryVirtualCard;
    }
    return filter === CardFilters.COMPANY ? companyCards : userCards;
  };

  const definitivelyBlockedFilteredCards = filteredCards()
    // @ts-expect-error card.status is possibly 'undefined'.
    .filter((card) => DEFINITIVELY_BLOCKED_STATUS_CODES.includes(card.status))
    // @ts-expect-error card.status is possibly 'undefined'.
    .sort(sortByStatusCodes(DEFINITIVELY_BLOCKED_STATUS_CODES));

  const notDefinitivelyBlockedFilteredCards = filteredCards()
    .filter((card) =>
      // @ts-expect-error card.status is possibly 'undefined'.
      NOT_DEFINITIVELY_BLOCKED_STATUS_CODES.includes(card.status),
    )
    // @ts-expect-error card.status is possibly 'undefined'.
    .sort(sortByStatusCodes(NOT_DEFINITIVELY_BLOCKED_STATUS_CODES));

  // only display the filter bar if
  // - the user can see all the payment-cards of the company
  // - the company has more than one admin or employee
  const shouldSeeFilterBar =
    canAccessFeature({ name: FeatureName.Paymentcards }) && !isUniqueMember;

  const shouldDisplayCardOwnerName =
    filter === CardFilters.COMPANY &&
    canAccessFeature({ name: FeatureName.Paymentcards }) &&
    !isUniqueMember;

  const orderOrRequestPaymentCard = () => {
    let link;
    let text;
    if (canOrderPaymentCards) {
      link = routes.orderNew;
      text = locales.orderNewCard;
    }
    if (canRequestPaymentCards) {
      link = routes.requestNew;
      text = locales.requestNewCard;
    }

    if (link === undefined) {
      return null;
    }

    return (
      <Button
        as={Link}
        icon="plus"
        iconPosition="left"
        to={link}
        variant="secondary"
      >
        {text}
      </Button>
    );
  };

  return (
    <Layout.Root>
      <Layout.Body size="md">
        {state?.shouldDisplayBackButton ? (
          <BackButton marginBottom={32} />
        ) : null}
        <Layout.Header
          action={orderOrRequestPaymentCard()}
          title={locales.title}
        />

        {/**
         * For an AH/Admin, we should disable the "My cards" section when they
         * have no card.
         */}
        {shouldSeeFilterBar ? (
          <FilterBar
            disableOwnCardsFilter={userCards.length === 0}
            filter={filter}
            marginBottom="space-32"
            setFilter={(nextFilter) => {
              setFilter(nextFilter);
              // reset selected card
              history.push(routes.root);
            }}
          />
        ) : null}

        {isListEmpty ? (
          <PaymentCardListEmptyState />
        ) : (
          <SunshineCard.Group>
            {notDefinitivelyBlockedFilteredCards.map((creditCard) => (
              <PaymentCardRow
                // @ts-expect-error creditCard.status is possibly 'undefined'.
                creditCard={creditCard}
                key={creditCard.creditCardId}
                shouldDisplayCardOwnerName={shouldDisplayCardOwnerName}
              />
            ))}

            {/**
             * We do not show the ongoing payment cards requests for the AH/Admins
             * on the "My cards" section.
             */}
            {shouldSeeFilterBar && filter === CardFilters.OWN
              ? null
              : ongoingPaymentCardsRequests.map((ongoingPaymentCardRequest) => (
                  <OngoingPaymentCardRequestRow
                    key={ongoingPaymentCardRequest.teamMembersRequestId}
                    ongoingPaymentCardRequest={ongoingPaymentCardRequest}
                  />
                ))}

            {definitivelyBlockedFilteredCards.map((creditCard) => (
              <PaymentCardRow
                // @ts-expect-error creditCard.status is possibly 'undefined'.
                creditCard={creditCard}
                key={creditCard.creditCardId}
                shouldDisplayCardOwnerName={shouldDisplayCardOwnerName}
              />
            ))}
          </SunshineCard.Group>
        )}
      </Layout.Body>

      {selectedCreditCard ? (
        <Layout.Sidebar
          borderLeft="1px solid"
          borderLeftColor="border"
          isScrollable
        >
          {/* @ts-expect-error selectedCreditCard.status is possibly 'undefined'. */}
          <PaymentCardSidebar creditCard={selectedCreditCard} />
        </Layout.Sidebar>
      ) : null}

      {selectedOngoingPaymentCardRequest ? (
        <Layout.Sidebar isScrollable>
          <OngoingRequestSidebar request={selectedOngoingPaymentCardRequest} />
        </Layout.Sidebar>
      ) : null}
    </Layout.Root>
  );
};

export default PaymentCardList;
